import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Clock, MapPin, Phone, CheckCircle, ArrowRight, Tag, Stethoscope } from 'lucide-react';

const CAMPS = [
  {
    id: 'diabetes-screening-sep',
    title_en: 'Complete Diabetes Screening Camp',
    title_ta: 'முழுமையான நீரிழிவு பரிசோதனை முகாம்',
    tagline_en: '20 Vayasu Dhaane... Diabetes Varaadhu-nu Ninaikireengala?',
    tagline_ta: '20 வயசு தானே... நீரிழிவு வராது-னு நினைக்கிறீர்களா?',
    price: 599,
    color: '#0066b3',
    accent: '#c41e3a',
    schedule: [
      {
        date_en: '26 Sept',
        date_ta: '26 செப்டம்பர்',
        time_en: '6:00 AM – 9:00 AM',
        time_ta: 'காலை 6:00 – 9:00',
        desc_en: 'Blood Collection (Empty Stomach Required)',
        desc_ta: 'இரத்த சேகரிப்பு (வெறும் வயிற்றில் வரவும்)',
      },
      {
        date_en: '27 Sept',
        date_ta: '27 செப்டம்பர்',
        time_en: '9:00 AM – 1:00 PM',
        time_ta: 'காலை 9:00 – மதியம் 1:00',
        desc_en: 'Physician Consultation',
        desc_ta: 'மருத்துவர் ஆலோசனை',
      },
    ],
    inclusions_en: [
      'Height, Weight & BMI',
      'BP, FBS, PPBS & HbA1c',
      'Lipid Profile, TSH, AST & ALT',
      'Serum Creatinine',
    ],
    inclusions_ta: [
      'உயரம், எடை & BMI',
      'BP, FBS, PPBS & HbA1c',
      'லிப்பிட் புரொஃபைல், TSH, AST & ALT',
      'சீரம் கிரியேட்டினின்',
    ],
    offers_en: ['FREE Physician Consultation', '10% OFF on Medicines'],
    offers_ta: ['இலவச மருத்துவர் ஆலோசனை', 'மருந்துகளில் 10% தள்ளுபடி'],
    active: true,
  },
];

const CampsSection = () => {
  const { language } = useLanguage();

  const activeCamps = CAMPS.filter((c) => c.active);
  if (activeCamps.length === 0) return null;

  const scrollToAppointment = () => {
    const el = document.getElementById('appointment');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="camps" className="py-10 bg-gradient-to-b from-[#eef4fb] to-white" data-testid="camps-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#c41e3a]/10 text-[#c41e3a] rounded-full text-xs font-bold uppercase tracking-wider mb-3">
            <Stethoscope size={14} />
            {language === 'ta' ? 'சிறப்பு முகாம்கள்' : 'Special Health Camps'}
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f172a] tracking-tight">
            {language === 'ta' ? 'வரவிருக்கும் சுகாதார முகாம்கள்' : 'Upcoming Health Camps'}
          </h2>
        </div>

        {/* Camp Cards */}
        <div className="space-y-6">
          {activeCamps.map((camp) => (
            <div
              key={camp.id}
              className="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
              data-testid={`camp-card-${camp.id}`}
            >
              {/* Top accent bar */}
              <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${camp.color}, ${camp.accent})` }} />

              <div className="p-6 md:p-8">
                {/* Header row */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div className="flex-1">
                    <p className="text-sm font-semibold italic text-[#c41e3a] mb-1" data-testid="camp-tagline">
                      {language === 'ta' ? camp.tagline_ta : camp.tagline_en}
                    </p>
                    <h3 className="text-xl md:text-2xl font-extrabold text-[#0f172a]" data-testid="camp-title">
                      {language === 'ta' ? camp.title_ta : camp.title_en}
                    </h3>
                  </div>
                  {/* Price badge */}
                  <div className="flex-shrink-0 text-center bg-gradient-to-br from-[#0066b3] to-[#004a82] text-white rounded-xl px-5 py-3 shadow-md" data-testid="camp-price">
                    <div className="text-3xl font-extrabold leading-none">
                      <span className="text-lg">&#8377;</span>{camp.price}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80 mt-0.5">
                      {language === 'ta' ? 'மட்டுமே' : 'Only'}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left: Inclusions + Offers */}
                  <div>
                    {/* Offers */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(language === 'ta' ? camp.offers_ta : camp.offers_en).map((offer) => (
                        <span
                          key={offer}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c41e3a]/8 text-[#c41e3a] rounded-lg text-xs font-bold"
                        >
                          <Tag size={12} /> {offer}
                        </span>
                      ))}
                    </div>

                    {/* Inclusions */}
                    <div className="bg-[#f8fafc] rounded-xl p-4 border border-gray-100">
                      <h4 className="text-xs font-bold text-[#64748b] uppercase tracking-wider mb-3">
                        {language === 'ta' ? 'சேர்க்கைகள்' : 'Includes'}
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {(language === 'ta' ? camp.inclusions_ta : camp.inclusions_en).map((item) => (
                          <div key={item} className="flex items-center gap-2">
                            <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                            <span className="text-sm text-[#3a4a5c]">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: Schedule + CTA */}
                  <div className="flex flex-col">
                    {/* Schedule */}
                    <div className="space-y-3 mb-5">
                      {camp.schedule.map((slot) => (
                        <div key={slot.date_en} className="bg-[#0066b3]/5 rounded-xl p-4 border border-[#0066b3]/10">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar size={14} className="text-[#0066b3]" />
                            <span className="text-sm font-bold text-[#0066b3]">
                              {language === 'ta' ? slot.date_ta : slot.date_en}
                            </span>
                            <span className="text-[#64748b] text-xs">|</span>
                            <Clock size={12} className="text-[#64748b]" />
                            <span className="text-xs text-[#64748b] font-medium">
                              {language === 'ta' ? slot.time_ta : slot.time_en}
                            </span>
                          </div>
                          <p className="text-xs text-[#5a6b7c] ml-5">
                            {language === 'ta' ? slot.desc_ta : slot.desc_en}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-xs text-[#5a6b7c] mb-4">
                      <MapPin size={14} className="text-[#0066b3] flex-shrink-0" />
                      <span>Sakthi Hospital, 175 Big Street, Triplicane, Chennai - 600005</span>
                    </div>

                    {/* CTA buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                      <button
                        onClick={() => {
                          scrollToAppointment();
                          if (window.dataLayer) {
                            window.dataLayer.push({ event: 'camp_registration_click', camp_name: camp.title_en, camp_price: camp.price });
                          }
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#c41e3a] hover:bg-[#a0182f] text-white rounded-xl font-bold text-sm transition-colors shadow-md shadow-[#c41e3a]/20"
                        data-testid="camp-book-btn"
                      >
                        {language === 'ta' ? 'இப்போது பதிவு செய்யுங்கள்' : 'Register Now'}
                        <ArrowRight size={16} />
                      </button>
                      <a
                        href={`https://wa.me/919840264444?text=${encodeURIComponent(language === 'ta' ? `வணக்கம்! ${camp.title_ta} முகாமில் பதிவு செய்ய விரும்புகிறேன்.` : `Hi! I'd like to register for the ${camp.title_en}.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-5 py-3 border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5 rounded-xl font-bold text-sm transition-colors"
                        data-testid="camp-whatsapp-btn"
                        onClick={() => {
                          if (window.dataLayer) window.dataLayer.push({ event: 'camp_whatsapp_click', camp_name: camp.title_en });
                        }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </a>
                    </div>

                    {/* Contact */}
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <Phone size={12} className="text-[#0066b3]" />
                      <a
                        href="tel:+914428442626"
                        className="text-xs font-semibold text-[#0066b3] hover:underline"
                        onClick={() => { if (window.dataLayer) window.dataLayer.push({ event: 'click_to_call', call_source: 'camp_section' }); }}
                      >
                        044 2844 2626
                      </a>
                      <span className="text-[#94a3b8] text-xs">|</span>
                      <a
                        href="tel:+919840264444"
                        className="text-xs font-semibold text-[#0066b3] hover:underline"
                      >
                        98402 64444
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CampsSection;
